import type { PlayerStats } from '../entities/Player'

/**
 * Definition for a single upgrade card.
 * `apply` is called once each time the card is chosen; `level` is how many
 * times the card has been picked *before* this call (0 on the first pick).
 */
export interface CardDef {
  id: string
  name: string
  /** Emoji used as the card icon in the level-up modal. */
  icon: string
  /** Short description shown on the card (≤ 40 chars recommended). */
  description: string
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  /** Broad category — used for synergy tracking. */
  type: 'weapon' | 'passive' | 'special'
  /** Element tag — used for element-based synergy tracking. */
  element?: 'fire' | 'lightning' | 'defense'
  /** Mutate player stats to apply the upgrade. */
  apply(stats: PlayerStats, level: number): void
}

// ─── Weapon Cards (9) ─────────────────────────────────────────────────────────

const fireball: CardDef = {
  id: 'fireball',
  name: 'Fireball',
  icon: '🔥',
  description: '+1 Fireball level (AoE burst)',
  rarity: 'rare',
  type: 'weapon',
  element: 'fire',
  apply(stats) {
    stats.fireballLevel += 1
  },
}

const lightning: CardDef = {
  id: 'lightning',
  name: 'Chain Lightning',
  icon: '⚡',
  description: '+1 Lightning level (chains to enemies)',
  rarity: 'rare',
  type: 'weapon',
  element: 'lightning',
  apply(stats) {
    stats.lightningLevel += 1
  },
}

const orbitingShield: CardDef = {
  id: 'orbiting_shield',
  name: 'Orbiting Shield',
  icon: '🌀',
  description: '+1 orbiting projectile',
  rarity: 'rare',
  type: 'weapon',
  element: 'defense',
  apply(stats) {
    stats.orbitCount += 1
  },
}

const spinAttack: CardDef = {
  id: 'spin_attack',
  name: 'Spin Attack',
  icon: '🌪️',
  description: '+1 Spin Attack level (360° melee)',
  rarity: 'rare',
  type: 'weapon',
  element: 'fire',
  apply(stats) {
    stats.spinAttackLevel += 1
  },
}

const frostNova: CardDef = {
  id: 'frost_nova',
  name: 'Frost Nova',
  icon: '❄️',
  description: '+1 Frost Nova level (slows + AoE)',
  rarity: 'rare',
  type: 'weapon',
  apply(stats) {
    stats.frostNovaLevel += 1
  },
}

const arrowRain: CardDef = {
  id: 'arrow_rain',
  name: 'Arrow Rain',
  icon: '🏹',
  description: '+1 Arrow Rain level (volley from sky)',
  rarity: 'epic',
  type: 'weapon',
  element: 'lightning',
  apply(stats) {
    stats.arrowRainLevel += 1
  },
}

const boomerang: CardDef = {
  id: 'boomerang',
  name: 'Boomerang',
  icon: '🪃',
  description: '+1 Boomerang level (bounces between foes)',
  rarity: 'rare',
  type: 'weapon',
  element: 'lightning',
  apply(stats) {
    stats.boomerangLevel += 1
  },
}

const laserBeam: CardDef = {
  id: 'laser_beam',
  name: 'Laser Beam',
  icon: '🔆',
  description: '+1 Laser Beam level (piercing line)',
  rarity: 'epic',
  type: 'weapon',
  element: 'fire',
  apply(stats) {
    stats.laserBeamLevel += 1
  },
}

const poisonCloud: CardDef = {
  id: 'poison_cloud',
  name: 'Poison Cloud',
  icon: '☠️',
  description: '+1 Poison Cloud level (DoT area)',
  rarity: 'rare',
  type: 'weapon',
  apply(stats) {
    stats.poisonCloudLevel += 1
  },
}

// ─── Passive Cards (16) ───────────────────────────────────────────────────────

const hpUp: CardDef = {
  id: 'hp_up',
  name: 'HP Up',
  icon: '❤️',
  description: '+25 max HP (heals 25 too)',
  rarity: 'common',
  type: 'passive',
  apply(stats) {
    stats.maxHp += 25
    stats.hp = Math.min(stats.hp + 25, stats.maxHp)
  },
}

const speedUp: CardDef = {
  id: 'speed_up',
  name: 'Speed Up',
  icon: '👟',
  description: '+15% movement speed',
  rarity: 'common',
  type: 'passive',
  apply(stats) {
    stats.speed = Math.round(stats.speed * 1.15)
  },
}

const damageUp: CardDef = {
  id: 'damage_up',
  name: 'Damage Up',
  icon: '⚔️',
  description: '+20% damage per stack',
  rarity: 'common',
  type: 'passive',
  apply(stats) {
    stats.damage = Math.round(stats.damage * 1.2)
  },
}

const attackSpeed: CardDef = {
  id: 'attack_speed',
  name: 'Attack Speed',
  icon: '🏃',
  description: '+20% attacks per second',
  rarity: 'common',
  type: 'passive',
  apply(stats) {
    stats.attackSpeed = Math.round(stats.attackSpeed * 1.2 * 10) / 10
  },
}

const regen: CardDef = {
  id: 'regen',
  name: 'Regen',
  icon: '🌿',
  description: '+2 HP restored per second',
  rarity: 'common',
  type: 'passive',
  apply(stats) {
    stats.regen += 2
  },
}

const armor: CardDef = {
  id: 'armor',
  name: 'Armor',
  icon: '🛡️',
  description: '+5 flat damage reduction',
  rarity: 'rare',
  type: 'passive',
  element: 'defense',
  apply(stats) {
    stats.armor += 5
  },
}

const crit: CardDef = {
  id: 'crit',
  name: 'Crit Chance',
  icon: '🎯',
  description: '+10% crit chance (2× damage)',
  rarity: 'rare',
  type: 'passive',
  apply(stats) {
    stats.critChance = Math.min(1, stats.critChance + 0.1)
  },
}

const magnet: CardDef = {
  id: 'magnet',
  name: 'Magnet',
  icon: '🧲',
  description: '+50% item pickup radius',
  rarity: 'common',
  type: 'passive',
  apply(stats) {
    stats.magnetRadius = Math.round(stats.magnetRadius * 1.5)
  },
}

const goldBonus: CardDef = {
  id: 'gold_bonus',
  name: 'Gold Rush',
  icon: '💰',
  description: '+25% gold from enemies',
  rarity: 'rare',
  type: 'passive',
  apply(stats) {
    stats.goldBonus = Math.round(stats.goldBonus * 1.25 * 100) / 100
  },
}

const xpBonus: CardDef = {
  id: 'xp_bonus',
  name: 'Scholar',
  icon: '📚',
  description: '+20% XP gain',
  rarity: 'common',
  type: 'passive',
  apply(stats) {
    stats.xpBonus = Math.round(stats.xpBonus * 1.2 * 100) / 100
  },
}

const lifeSteal: CardDef = {
  id: 'life_steal',
  name: 'Life Steal',
  icon: '🩸',
  description: '+5% of damage dealt restored as HP',
  rarity: 'rare',
  type: 'passive',
  apply(stats) {
    stats.lifeSteal = Math.round((stats.lifeSteal + 0.05) * 100) / 100
  },
}

const luck: CardDef = {
  id: 'luck',
  name: 'Luck',
  icon: '🍀',
  description: '+1 Luck (better card rarity odds)',
  rarity: 'rare',
  type: 'passive',
  apply(stats) {
    stats.luckBonus += 1
  },
}

const shieldCharge: CardDef = {
  id: 'shield_charge',
  name: 'Shield Charge',
  icon: '🔰',
  description: '+1 shield charge (absorbs one hit)',
  rarity: 'rare',
  type: 'passive',
  element: 'defense',
  apply(stats) {
    stats.shieldCharges += 1
  },
}

const thorns: CardDef = {
  id: 'thorns',
  name: 'Thorns',
  icon: '🌵',
  description: '+20% damage reflected to attacker',
  rarity: 'rare',
  type: 'passive',
  element: 'defense',
  apply(stats) {
    stats.thornsPercent = Math.min(0.8, Math.round((stats.thornsPercent + 0.2) * 100) / 100)
  },
}

const doubleStrike: CardDef = {
  id: 'double_strike',
  name: 'Double Strike',
  icon: '👊',
  description: '+15% chance to hit twice',
  rarity: 'epic',
  type: 'passive',
  apply(stats) {
    stats.doubleStrikeChance = Math.min(0.9, Math.round((stats.doubleStrikeChance + 0.15) * 100) / 100)
  },
}

const timeWarp: CardDef = {
  id: 'time_warp',
  name: 'Time Warp',
  icon: '⏱️',
  description: '+15% slow aura on nearby enemies',
  rarity: 'epic',
  type: 'passive',
  apply(stats) {
    stats.timeWarpBonus = Math.min(0.75, Math.round((stats.timeWarpBonus + 0.15) * 100) / 100)
  },
}

// ─── Special Cards (6) ────────────────────────────────────────────────────────

const levelSkip: CardDef = {
  id: 'level_skip',
  name: 'Level Skip',
  icon: '⏩',
  description: '+50% XP gain for the rest of the run',
  rarity: 'epic',
  type: 'special',
  apply(stats) {
    stats.xpBonus = Math.round(stats.xpBonus * 1.5 * 100) / 100
  },
}

const extraLife: CardDef = {
  id: 'extra_life',
  name: 'Extra Life',
  icon: '💖',
  description: 'Revive once with 50% HP on death',
  rarity: 'legendary',
  type: 'special',
  apply(stats) {
    stats.hasExtraLife = true
    stats.extraLifeUsed = false
  },
}

const bossKiller: CardDef = {
  id: 'boss_killer',
  name: 'Boss Killer',
  icon: '💀',
  description: '+50% damage vs bosses',
  rarity: 'epic',
  type: 'special',
  apply(stats) {
    stats.bossKillerActive = true
  },
}

const timeSlow: CardDef = {
  id: 'time_slow',
  name: 'Time Slow',
  icon: '⏸️',
  description: 'All enemies move 40% slower (permanent)',
  rarity: 'epic',
  type: 'special',
  apply(stats) {
    stats.timeSlowActive = true
  },
}

const nuclearBomb: CardDef = {
  id: 'nuclear_bomb',
  name: 'Nuclear Bomb',
  icon: '💣',
  description: 'Once per run: massive screen-wide explosion',
  rarity: 'legendary',
  type: 'special',
  apply(stats) {
    stats.nuclearBombActive = true
  },
}

const shieldBash: CardDef = {
  id: 'shield_bash',
  name: 'Shield Bash',
  icon: '🔨',
  description: 'Orbiting shields deal 3× impact damage',
  rarity: 'epic',
  type: 'special',
  apply(stats) {
    stats.shieldBashActive = true
  },
}

/**
 * Full card pool — 31 cards across 3 categories.
 * Cards are sampled via rarity-weighted selection for the level-up modal.
 */
export const CARDS: CardDef[] = [
  // Weapons (9)
  fireball,
  lightning,
  orbitingShield,
  spinAttack,
  frostNova,
  arrowRain,
  boomerang,
  laserBeam,
  poisonCloud,
  // Passives (16)
  hpUp,
  speedUp,
  damageUp,
  attackSpeed,
  regen,
  armor,
  crit,
  magnet,
  goldBonus,
  xpBonus,
  lifeSteal,
  luck,
  shieldCharge,
  thorns,
  doubleStrike,
  timeWarp,
  // Specials (6)
  levelSkip,
  extraLife,
  bossKiller,
  timeSlow,
  nuclearBomb,
  shieldBash,
]

// ─── Rarity Weights ───────────────────────────────────────────────────────────

/**
 * Base drop weights for each rarity tier.
 * Common 60%, Rare 25%, Epic 12%, Legendary 3%.
 */
const BASE_RARITY_WEIGHTS: Record<CardDef['rarity'], number> = {
  common: 60,
  rare: 25,
  epic: 12,
  legendary: 3,
}

/**
 * Pick a rarity tier based on weighted probabilities.
 * `luckBonus` (integer ≥ 0) shifts weight toward higher rarities:
 * each point reduces common by 5 and distributes the gain upward.
 */
function pickWeightedRarity(luckBonus = 0): CardDef['rarity'] {
  const luck = Math.max(0, Math.floor(luckBonus))
  const weights = {
    common: Math.max(10, BASE_RARITY_WEIGHTS.common - luck * 5),
    rare: BASE_RARITY_WEIGHTS.rare + luck * 2,
    epic: Math.min(35, BASE_RARITY_WEIGHTS.epic + luck * 2),
    legendary: Math.min(20, BASE_RARITY_WEIGHTS.legendary + luck),
  }

  const total = Object.values(weights).reduce((a, b) => a + b, 0)
  let roll = Math.random() * total
  for (const [rarity, weight] of Object.entries(weights) as [CardDef['rarity'], number][]) {
    roll -= weight
    if (roll <= 0) return rarity
  }
  return 'common'
}

// ─── Synergy Definitions ──────────────────────────────────────────────────────

/**
 * Synergy: triggered when a card reaches the required stack count (card-based)
 * or when the player owns enough distinct cards of the same element (element-based).
 * `apply` is called once per trigger (not every frame).
 */
export interface SynergyDef {
  /** The card id that triggers this synergy (card-stack-based). */
  cardId?: string
  /** Element that triggers this synergy (element-based, counts distinct cards). */
  element?: 'fire' | 'lightning' | 'defense'
  /** Minimum stacks / distinct cards needed to activate. */
  threshold: number
  /** Human-readable label shown in the HUD or tooltip. */
  label: string
  apply(stats: PlayerStats): void
}

export const SYNERGIES: SynergyDef[] = [
  // ── Element synergies (3 distinct cards of same element) ──────────────────
  {
    element: 'fire',
    threshold: 3,
    label: '🔥 Inferno — +50% fire dmg',
    apply(stats) {
      stats.fireDamageBonus += 0.5
    },
  },
  {
    element: 'lightning',
    threshold: 3,
    label: '⚡ Storm — +5 chain targets',
    apply(stats) {
      stats.lightningChainBonus += 5
    },
  },
  {
    element: 'defense',
    threshold: 3,
    label: '🛡️ Fortress — +1 shield charge & defense',
    apply(stats) {
      stats.shieldCharges += 1
      stats.defenseBonus += 1
    },
  },
  // ── Card-stack synergies (same card picked multiple times) ────────────────
  {
    cardId: 'orbiting_shield',
    threshold: 3,
    label: '🌀 Orbital — +1 bonus orbit',
    apply(stats) {
      stats.orbitCountBonus += 1
    },
  },
  {
    cardId: 'spin_attack',
    threshold: 3,
    label: '🌪️ Cyclone — +30% spin damage',
    apply(stats) {
      stats.damage = Math.round(stats.damage * 1.3)
    },
  },
  {
    cardId: 'arrow_rain',
    threshold: 3,
    label: '🏹 Hailstorm — +2 arrow rain levels',
    apply(stats) {
      stats.arrowRainLevel += 2
    },
  },
  {
    cardId: 'frost_nova',
    threshold: 3,
    label: '❄️ Blizzard — +2 frost nova levels',
    apply(stats) {
      stats.frostNovaLevel += 2
    },
  },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Look up a card by its id. Returns undefined if not found. */
export function getCardById(id: string): CardDef | undefined {
  return CARDS.find((c) => c.id === id)
}

/**
 * Return `count` unique random cards from the pool using rarity-weighted selection.
 * `luckBonus` (integer ≥ 0) boosts the chance of higher-rarity cards.
 */
export function sampleCards(count: number, luckBonus = 0): CardDef[] {
  const pool = [...CARDS]
  const result: CardDef[] = []

  while (result.length < count && pool.length > 0) {
    const targetRarity = pickWeightedRarity(luckBonus)
    const rarityPool = pool.filter((c) => c.rarity === targetRarity)

    if (rarityPool.length > 0) {
      const idx = Math.floor(Math.random() * rarityPool.length)
      const card = rarityPool[idx]
      result.push(card)
      pool.splice(pool.indexOf(card), 1)
    } else {
      // Fall back to any remaining card when the target rarity is exhausted
      const idx = Math.floor(Math.random() * pool.length)
      result.push(pool.splice(idx, 1)[0])
    }
  }

  return result
}
